import { createRoot, type Root } from 'react-dom/client';
import { createElement } from 'react';
import { Designer } from './Designer';
import { createMemoryAdapter } from './adapters/memory';
import type { DesignerPorts } from './types';
import type { DesignerUiTheme } from './lib/uiTheme';
import { normalizeThemeHex } from './lib/uiTheme';
import cssText from './styles.css?inline';

type SaveResult = { designId: string; versionId: string; objectCount: number };

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('ox-designer-fonts')) {
    const link = document.createElement('link');
    link.id = 'ox-designer-fonts';
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Acme&family=Anton&family=Bangers&family=Bebas+Neue&family=Oswald:wght@500;700&family=Pacifico&family=Rubik:wght@500;700&display=swap';
    document.head.appendChild(link);
  }
  if (document.getElementById('ox-designer-styles')) return;
  const style = document.createElement('style');
  style.id = 'ox-designer-styles';
  style.textContent = cssText;
  document.head.appendChild(style);
}

function themeFromAttributes(el: HTMLElement): DesignerUiTheme | undefined {
  const accent = normalizeThemeHex(el.getAttribute('accent-color'));
  const onAccent = normalizeThemeHex(el.getAttribute('on-accent-color'));
  const highlight = normalizeThemeHex(el.getAttribute('highlight-color'));
  if (!accent && !onAccent && !highlight) return undefined;
  return {
    ...(accent ? { accent } : {}),
    ...(onAccent ? { onAccent } : {}),
    ...(highlight ? { highlight } : {}),
  };
}

class OxDesignerElement extends HTMLElement {
  static get observedAttributes() {
    return [
      'model',
      'color',
      'design',
      'api-base',
      'adapter',
      'logo',
      'logo-href',
      'data-ox-action',
      'commerce',
      'gallery-code',
      'store-id',
      'accent-color',
      'on-accent-color',
      'highlight-color',
    ];
  }

  #root: Root | null = null;
  #ports: DesignerPorts | null = null;
  #saveApi: (() => Promise<SaveResult | null>) | null = null;
  #assertReady: (() => void) | null = null;

  connectedCallback() {
    ensureStyles();
    if (!this.#root) {
      const mount = document.createElement('div');
      mount.style.height = '100%';
      this.appendChild(mount);
      this.#root = createRoot(mount);
    }
    this.render();
  }

  disconnectedCallback() {
    this.#root?.unmount();
    this.#root = null;
    this.#saveApi = null;
    this.#assertReady = null;
  }

  attributeChangedCallback() {
    this.render();
  }

  /** Inject custom commerce ports (Woo adapter, etc.) */
  setPorts(ports: DesignerPorts) {
    this.#ports = ports;
    this.render();
  }

  /** Persist current canvas. Used by Ox Studio “Continuar a Detalles”. */
  async saveDesign(): Promise<SaveResult | null> {
    if (!this.#saveApi) return null;
    return this.#saveApi();
  }

  /** Throws if design step requirements are incomplete (print type, etc.). */
  assertReady(): void {
    this.#assertReady?.();
  }

  private render() {
    if (!this.#root) return;
    const apiBase = this.getAttribute('api-base') ?? 'http://localhost:4010';
    // Empty attribute must not win — "" breaks getModel("").
    const modelId = this.getAttribute('model') || undefined;
    const colorKey = this.getAttribute('color') || undefined;
    const designId = this.getAttribute('design') || undefined;
    const designAction = this.getAttribute('data-ox-action') || undefined;
    const galleryCode = this.getAttribute('gallery-code') || undefined;
    const storeId = this.getAttribute('store-id') || undefined;
    const adapter = this.getAttribute('adapter') ?? 'memory';
    const logoUrl = this.getAttribute('logo') || undefined;
    const logoHref = this.getAttribute('logo-href') || undefined;
    const commerceAttr = (this.getAttribute('commerce') ?? '1').toLowerCase();
    const commerce = !(commerceAttr === '0' || commerceAttr === 'false' || commerceAttr === 'off');
    const uiTheme = themeFromAttributes(this);

    // Woo (and other remote adapters) inject ports via setPorts(); do not fall back to
    // MemoryAdapter or getModel(185) throws before the shortcode script runs.
    if (adapter !== 'memory' && !this.#ports) {
      this.#root.render(
        createElement(
          'div',
          {
            style: {
              display: 'grid',
              placeItems: 'center',
              height: '100%',
              fontFamily: 'system-ui, sans-serif',
              color: '#555',
            },
          },
          'Cargando diseñador…',
        ),
      );
      return;
    }

    if (!modelId && !designId) {
      this.#root.render(
        createElement(
          'div',
          {
            style: {
              display: 'grid',
              placeItems: 'center',
              height: '100%',
              fontFamily: 'system-ui, sans-serif',
              color: '#555',
              padding: '24px',
              textAlign: 'center',
            },
          },
          'Elige un producto para personalizar.',
        ),
      );
      return;
    }

    const ports = this.#ports ?? createMemoryAdapter(apiBase);
    this.#root.render(
      createElement(Designer, {
        modelId,
        colorKey,
        designId,
        designAction: designAction || undefined,
        galleryCode: galleryCode || undefined,
        storeId: storeId || undefined,
        apiBase,
        logoUrl,
        logoHref,
        commerce,
        uiTheme,
        ports,
        registerSaveApi: (api) => {
          this.#saveApi = api ? () => api.save() : null;
          this.#assertReady = api?.assertReady ?? null;
        },
      }),
    );
  }
}

if (!customElements.get('ox-designer')) {
  customElements.define('ox-designer', OxDesignerElement);
}

export { OxDesignerElement };
